import { apiClient, createApiClient } from '../../../../api/apiClient';

const api = createApiClient();
const BASE = '/analytics/wes-pallet-staging';

// ─── Request (mirrors the Analytics Hub WesPalletStagingReadRequest) ─────────────────────────────

export type WesApiStagingScope = 'ALL' | 'AUDIT_RELATED' | 'OTHER';
export type WesApiHierarchy = 'REGION' | 'MARKET' | 'LOCATION';
export type WesApiTimeGrain = 'YEAR' | 'PERIOD' | 'WEEK';
export type WesApiDimension = 'REGION' | 'MARKET' | 'LOCATION' | 'LOCATION_ID' | 'YEAR' | 'PERIOD' | 'WEEK';
export type WesApiSortField = 'TOTAL' | 'STAGED' | 'STAGED_OTHER' | 'STAGED_AUDIT_RELATED';
export type WesApiSortDirection = 'ASC' | 'DESC';
export type WesApiDisplayMode = 'PERCENT' | 'PALLETS';

export interface WesApiFilters {
  regions?: string[];
  markets?: string[];
  locations?: string[];
  years?: number[];
  periods?: number[];
  // Fiscal week-in-year, not week-in-period.
  weeks?: number[];
}

export interface WesApiReadRequest {
  filters?: WesApiFilters;
  stagingScope?: WesApiStagingScope;
  hierarchy?: WesApiHierarchy;
  timeGrain?: WesApiTimeGrain;
  dimensions?: WesApiDimension[];
  sortField?: WesApiSortField;
  sortDirection?: WesApiSortDirection;
  displayMode?: WesApiDisplayMode;
  // Zero-based; location detail only.
  page?: number;
  // Rows per page; location detail only.
  size?: number;
}

// ─── Responses (percentages are unrounded 0-100; null when the total is 0 or the scope is unavailable) ──

export interface WesApiCounts {
  total: number;
  staged: number;
  notStaged: number;
  // Null until the audit mapping is approved.
  stagedOther: number | null;
  stagedAuditRelated: number | null;
  qualityAudit?: number | null;
  shortAudit?: number | null;
}

export interface WesApiGroup {
  key: string;
  label: string;
  counts: WesApiCounts;
  stagedPct: number | null;
}

export interface WesApiOverview {
  totals: WesApiCounts;
  stagedPct: number | null;
  highest: WesApiGroup | null;
  lowest: WesApiGroup | null;
}

export interface WesApiDistribution {
  notStaged: number;
  stagedAuditRelated: number | null;
  stagedOther: number | null;
  total: number;
  notStagedPct: number;
  stagedAuditRelatedPct: number | null;
  stagedOtherPct: number | null;
}

export interface WesApiGeography {
  hierarchy: WesApiHierarchy;
  groups: WesApiGroup[];
}

export interface WesApiTrendPoint {
  bucket: string;
  counts: WesApiCounts;
  stagedPct: number | null;
}

export interface WesApiTrendSeries {
  label: string;
  points: WesApiTrendPoint[];
}

export interface WesApiTrend {
  hierarchy: WesApiHierarchy;
  timeGrain: WesApiTimeGrain;
  buckets: string[];
  series: WesApiTrendSeries[];
}

export interface WesApiDetailRow {
  // Keyed by dimension enum name; also carries LOCATION_ID when LOCATION is requested.
  dimensions: Record<string, string>;
  counts: WesApiCounts;
  stagedPct: number | null;
  stagedOtherPct: number | null;
  stagedAuditRelatedPct: number | null;
}

export interface WesApiDetailPage {
  rows: WesApiDetailRow[];
  page: number;
  size: number;
  totalGroups: number;
  totalPages: number;
  totals: WesApiCounts;
}

export interface WesApiExport {
  rows: WesApiDetailRow[];
  totalGroups: number;
  totals: WesApiCounts;
}

export interface WesApiRefreshStatus {
  // UTC instant; null when the facts were never published.
  lastRefreshedAt: string | null;
  // ISO date (YYYY-MM-DD).
  dataThroughDate: string | null;
}

// ─── Calls ───────────────────────────────────────────────────────────────────────────────────────

const post = <T>(path: string, body: WesApiReadRequest): Promise<T> =>
  apiClient(api, `${BASE}${path}`, 'POST', body) as Promise<T>;

export const fetchWesRefreshStatus = (): Promise<WesApiRefreshStatus> =>
  apiClient(api, `${BASE}/refresh-status`, 'GET') as Promise<WesApiRefreshStatus>;

export const fetchWesOverview = (request: WesApiReadRequest) => post<WesApiOverview>('/kpi-overview', request);
export const fetchWesDistribution = (request: WesApiReadRequest) => post<WesApiDistribution>('/distribution', request);
export const fetchWesGeography = (request: WesApiReadRequest) => post<WesApiGeography>('/geography', request);
export const fetchWesTrend = (request: WesApiReadRequest) => post<WesApiTrend>('/trend', request);
export const fetchWesLocationDetail = (request: WesApiReadRequest) => post<WesApiDetailPage>('/location-detail', request);
export const fetchWesExport = (request: WesApiReadRequest) => post<WesApiExport>('/export', request);
